"use client";

import { useState } from "react";
import { Menu, X, LogIn, ArrowRight, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  FaInstagram,
  FaFacebookF,
  FaLinkedinIn,
  FaYoutube,
  FaPodcast,
  FaSpotify,
} from "react-icons/fa";
import { RiTwitterXFill } from "react-icons/ri";

const MENU_ITEMS = [
  {
    name: "Home",
    to: "/",
  },
  {
    name: "Features",
    to: "/features",
  },
  {
    name: "High Value",
    to: "/high-value",
  },
  {
    name: "Pricing",
    to: "/pricing",
  },
  {
    name: "Blog",
    to: "/blog",
  },
  {
    name: "AI Videos",
    to: "https://ai.lvlup-app.com",
    external: true,
    isNew: true,
  },
  {
    name: "Contact",
    to: "/contact",
  },
];

const Layout = ({ children }: { children: React.ReactNode }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleNavigation = (
    e: React.MouseEvent<HTMLAnchorElement>,
    href: string,
  ) => {
    if (href.startsWith("#")) {
      // Handle anchor links with smooth scrolling
      e.preventDefault();
      const target = document.querySelector(href);
      if (target) {
        target.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    } else {
      // Handle page routes - let the browser navigate normally
      // No preventDefault() needed
    }
    setIsMenuOpen(false);
  };

  const navBackground = "bg-black";

  return (
    <div className="min-h-screen bg-background text-text-primary">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 ${navBackground}`}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            <a
              href="/"
              className="flex items-center"
              // onClick={(e) => {
              //   e.preventDefault();
              //   window.scrollTo({ top: 0, behavior: 'smooth' });
              // }}
            >
              <img
                src="/logo.png"
                alt="LVLUP Logo"
                width={130}
                height={48}
                className="h-12 w-auto"
              />
            </a>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-6">
              {MENU_ITEMS.map((item) => (
                <a
                  key={item.name}
                  href={item.to}
                  onClick={(e) =>
                    !item.external && handleNavigation(e, item.to)
                  }
                  className="text-white hover:text-primary transition-colors"
                  {...(item.external
                    ? { target: "_blank", rel: "noopener noreferrer" }
                    : {})}
                >
                  <span className="flex items-center gap-1.5">
                    {item.isNew && (
                      <Sparkles className="w-4 h-4 text-purple-400" />
                    )}
                    {item.name}
                  </span>
                </a>
              ))}
              <a
                href="https://dashboard.lvlup-app.com/auth/login"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  variant="outline"
                  className="text-white border-primary hover:bg-primary hover:text-white rounded-lg "
                >
                  Login
                </Button>
              </a>
              <a
                href="https://dashboard.lvlup-app.com/auth/register"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all duration-300 transform hover:scale-105">
                  Start Free Trial
                </Button>
              </a>
            </div>

            {/* Mobile Navigation Toggle */}
            <button
              className="md:hidden p-2"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            >
              {isMenuOpen ? (
                <X className="w-7 h-7" />
              ) : (
                <Menu className="w-7 h-7" />
              )}
            </button>
          </div>

          {/* Mobile Navigation Menu */}
          {isMenuOpen && (
            <motion.div
              className="md:hidden py-4"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <div className="flex flex-col space-y-3">
                {MENU_ITEMS.map((item) => (
                  <a
                    key={item.name}
                    href={item.to}
                    onClick={(e) =>
                      !item.external && handleNavigation(e, item.to)
                    }
                    className="px-4 py-2 text-white hover:text-primary flex items-center gap-2"
                    {...(item.external
                      ? { target: "_blank", rel: "noopener noreferrer" }
                      : {})}
                  >
                    {item.isNew && (
                      <Sparkles className="w-4 h-4 text-purple-400" />
                    )}
                    {item.name}
                  </a>
                ))}
                <a
                  href="https://dashboard.lvlup-app.com/auth/login"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    variant="outline"
                    className="text-white border-primary hover:bg-primary hover:text-white rounded-lg w-full"
                  >
                    Login
                  </Button>
                </a>
                <a
                  href="https://dashboard.lvlup-app.com/auth/register"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button className="rounded-lg bg-primary text-white hover:bg-primary/90 w-full">
                    Start Free Trial
                  </Button>
                </a>
              </div>
            </motion.div>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-20">{children}</main>

      {/* Footer */}
      <footer className="py-12 border-t border-white/10">
        <div className="container px-4 mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <img
                src="/logo.png"
                alt="LVLUP Logo"
                width={80}
                height={32}
                loading="lazy"
                className="h-8 w-auto mb-4"
              />
              <p className="text-text-secondary mb-4">
                Empowering coaches to deliver exceptional results
              </p>
              <div className="flex space-x-4">
                <a
                  href="https://www.instagram.com/lvlupapp/"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Instagram"
                  className="text-text-secondary hover:text-white transition-colors"
                >
                  <FaInstagram className="w-4 h-4" />
                </a>
                <a
                  href="https://www.facebook.com/lvlup.application"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Facebook"
                  className="text-text-secondary hover:text-white transition-colors"
                >
                  <FaFacebookF className="w-4 h-4" />
                </a>
                <a
                  href="https://twitter.com/lvlup_app"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Twitter"
                  className="text-text-secondary hover:text-white transition-colors"
                >
                  <RiTwitterXFill className="w-4 h-4" />
                </a>
                <a
                  href="https://www.linkedin.com/company/lvlupapp"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="LinkedIn"
                  className="text-text-secondary hover:text-white transition-colors"
                >
                  <FaLinkedinIn className="w-4 h-4" />
                </a>
                <a
                  href="https://www.youtube.com/@lvlupapp"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="YouTube"
                  className="text-text-secondary hover:text-white transition-colors"
                >
                  <FaYoutube className="w-4 h-4" />
                </a>
                <a
                  href="https://podcasts.apple.com/podcast/lvlup-podz/id1707803586"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Apple Podcast"
                  className="text-text-secondary hover:text-white transition-colors"
                >
                  <FaPodcast className="w-4 h-4" />
                </a>
                <a
                  href="https://open.spotify.com/show/4EXPLeVPaEn18W1Zaq6uCh?si=ndhhRXD0RXaDgQ7lIwSITQ"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Spotify"
                  className="text-text-secondary hover:text-white transition-colors"
                >
                  <FaSpotify className="w-4 h-4" />
                </a>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-text-secondary">
                <li>
                  <a
                    href="/features"
                    className="hover:text-white cursor-pointer"
                  >
                    Features
                  </a>
                </li>
                <li>
                  <a
                    href="/high-value"
                    className="hover:text-white cursor-pointer"
                  >
                    High Value
                  </a>
                </li>
                <li>
                  <a
                    href="/pricing"
                    className="hover:text-white cursor-pointer"
                  >
                    Pricing
                  </a>
                </li>
                <li>
                  <a
                    href="https://ai.lvlup-app.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-white cursor-pointer"
                  >
                    AI Videos
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-text-secondary">
                <li>
                  <a href="/blog" className="hover:text-white cursor-pointer">
                    Blog
                  </a>
                </li>
                <li>
                  <a
                    href="/contact"
                    className="hover:text-white cursor-pointer"
                  >
                    Contact
                  </a>
                </li>
                <li>
                  <a
                    href="/support"
                    className="hover:text-white cursor-pointer"
                  >
                    Support
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-text-secondary">
                <li>
                  <a href="/privacy" className="hover:text-white">
                    Privacy
                  </a>
                </li>
                <li>
                  <a href="/terms" className="hover:text-white">
                    Terms
                  </a>
                </li>
                <li>
                  <a href="/mobile-tos" className="hover:text-white">
                    Mobile Terms
                  </a>
                </li>
                <li>
                  <a href="/cookies" className="hover:text-white">
                    Cookies
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t border-white/10 text-center text-text-secondary">
            <p>&copy; {new Date().getFullYear()} LVLUP. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
